import { createBrowserRouter, RouterProvider, Outlet, Navigate, useLocation } from 'react-router-dom'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { AuthProvider, useAuth } from './context/AuthContext'
import { Toaster } from "sonner"
import { WebSocketProvider } from './context/WebSocketContext'
import { hasAdminMenuAccess } from './lib/adminPermissions'

import MainLayout from './components/layout/MainLayout'
import ErrorBoundary from './components/layout/ErrorBoundary'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import UserManagementPage from './pages/users/UserManagementPage'
import CompanyManagementPage from './pages/companies/CompanyManagementPage'
import SystemSettingsPage from './pages/system-settings/SystemSettingsPage'
import BusinessHoursSettings from './pages/system-settings/BusinessHoursSettings'
import AnalyticsPage from './pages/analytics/AnalyticsPage'
import MusicUploadPage from './pages/music/MusicUploadPage'
import ManagerLoginPage from './pages/auth/ManagerLoginPage'
import NewPlaylist from './pages/playlists/NewPlaylist'
import PlaylistPage from './pages/playlists/PlaylistPage'
import TaxonomyPage from './pages/taxonomy/TaxonomyPage'
import DevicePage from './pages/devices/DevicePage'
import DeviceLogsPage from './pages/device-logs/DeviceLogsPage'
import { announcementRoutes } from './routes/announcementRoutes'
import { bannerRoutes } from './routes/bannerRoutes'
import { aiAnnouncementRoutes } from './routes/aiAnnouncementRoutes'
import { aiMusicRoutes } from './routes/aiMusicRoutes'
import { announcementRuleRoutes } from './routes/announcementRuleRoutes'
import { emailTemplateRoutes } from './routes/emailTemplateRoutes'
import { ticketRoutes } from './routes/ticketRoutes'
import { helpArticleRoutes } from './routes/helpArticleRoutes'

import './styles/globals.css';

// Auth kontrol bileşeni
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, loading } = useAuth()

  if (loading) {
    return null
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  return <>{children}</>
}

// URL path'inden hangi cloud-super-admin menü izninin gerektiğini bulur.
// Sidebar.tsx'teki permissionKey'lerle birebir eşleşmelidir.
const PATH_PERMISSION_PREFIXES: [string, string][] = [
  ['/dashboard', 'dashboard'],
  ['/companies', 'companies'],
  ['/email-templates', 'email-templates'],
  ['/tickets', 'tickets'],
  ['/help-articles', 'help-articles'],
  ['/users', 'users'],
  ['/devices', 'devices'],
  ['/device-logs', 'device-logs'],
  ['/announcements', 'announcements'],
  ['/ai-announcements', 'ai-announcements'],
  ['/ai-music', 'ai-music'],
  ['/music', 'music-upload'],
  ['/playlists', 'playlists'],
  ['/taxonomy', 'taxonomy'],
  ['/notices', 'notices'],
  ['/analytics', 'analytics'],
  ['/system-settings', 'system-settings']
]

const getPermissionKeyForPath = (pathname: string): string | null => {
  const match = PATH_PERMISSION_PREFIXES.find(
    ([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
  return match ? match[1] : null
}

// Sidebar'ı (MainLayout) etkilemeden sadece içerik alanını izne göre kısıtlar.
const PermissionGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth()
  const location = useLocation()
  const permissionKey = getPermissionKeyForPath(location.pathname)

  if (permissionKey && !hasAdminMenuAccess(user, permissionKey)) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <h1 className="text-xl font-semibold mb-2">Erişim Yetkiniz Yok</h1>
        <p className="text-muted-foreground">Bu bölümü görüntüleme yetkiniz bulunmuyor.</p>
      </div>
    )
  }

  return <>{children}</>
}

const queryClient = new QueryClient()

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <AuthProvider>
        <WebSocketProvider>
          <ProtectedRoute>
            <MainLayout>
              <PermissionGuard>
                <Outlet />
              </PermissionGuard>
            </MainLayout>
          </ProtectedRoute>
        </WebSocketProvider>
      </AuthProvider>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: '/',
        element: <Navigate to="/dashboard" replace />
      },
      {
        path: '/dashboard',
        element: <DashboardPage />
      },
      {
        path: '/companies',
        element: <CompanyManagementPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: '/users',
        element: <UserManagementPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: '/devices',
        element: <DevicePage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: '/device-logs',
        element: <DeviceLogsPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: '/analytics',
        element: <AnalyticsPage />
      },
      {
        path: '/system-settings',
        element: <SystemSettingsPage />
      },
      {
        path: '/system-settings/business-hours',
        element: <BusinessHoursSettings />
      },
      {
        path: '/music/upload',
        element: <MusicUploadPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: '/playlists',
        element: <PlaylistPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: '/playlists/new',
        element: <NewPlaylist />,
        errorElement: <ErrorBoundary />
      },
      {
        path: '/taxonomy',
        element: <TaxonomyPage />,
        errorElement: <ErrorBoundary />
      },
      ...announcementRoutes,
      ...bannerRoutes,
      ...aiAnnouncementRoutes,
      ...aiMusicRoutes,
      ...announcementRuleRoutes,
      ...emailTemplateRoutes,
      ...ticketRoutes,
      ...helpArticleRoutes
    ]
  },
  {
    path: '/login',
    element: (
      <AuthProvider>
        <LoginPage />
      </AuthProvider>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/register',
    element: (
      <AuthProvider>
        <RegisterPage />
      </AuthProvider>
    ),
    errorElement: <ErrorBoundary />
  },
  {
    path: '/manager-login',
    element: (
      <AuthProvider>
        <ManagerLoginPage />
      </AuthProvider>
    ),
    errorElement: <ErrorBoundary />
  }
])

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster richColors position="top-right" />
    </QueryClientProvider>
  )
}

export default App
